import { Grid, Paper, Stack, Typography, useTheme } from "@mui/material";
import { TitledSection } from "components/Classifiers/Common/TitledSection";
import { observer } from "mobx-react";
import { TestLLMClassifierOnConversationsResponse } from "models/LLMs/Testing/TestLLMClassifierOnConversationsResponse";
import { ErrorResultsSection } from "./ErrorResultsSection";
import { SuccessfulResultsSection } from "./SuccessfulResultsSection";
import { MetadataResultsCards } from "./MetadataResultsCards";
import { ScrollToResultButton } from "../ScrollToResultButton";
import { getFriendlyId } from "../utils";
import { Error } from "@mui/icons-material";
import { LLMClassifierBuilderStore } from "components/Classifiers/LLMClassifiers/Stores/LLMClassifierBuilderStore";
import { answerColorToThemeColor } from "components/Evaluation/Utils";
import { useStore } from "utils/useStore";
import { ExportToCSV } from "components/CSVExport/ExportToCSV";
import { LLMClassifierPurpose } from "models/LLMs/LLMClassifierPurpose";
import { ExtendedMetadataClassifier } from "models/ClassifierModel";
import { ResultBadge } from "../ResultBadge";

interface Props {
    data: TestLLMClassifierOnConversationsResponse;
}

export const OnConversationsTestingResults = observer(({ data }: Props) => {
    const store = useStore(LLMClassifierBuilderStore);

    switch (store.classifier.implementation.llmPurpose) {
        case LLMClassifierPurpose.ExtractData:
            return <MetadataResultsView data={data} />;
        default:
            return <GroupedClassifierResultsView data={data} />;
    }
});

const GroupedClassifierResultsView = observer(({ data }: Props) => {
    const theme = useTheme();
    const store = useStore(LLMClassifierBuilderStore);

    const outputs = store.classifier.outputs;
    const getDecisionResults = (decision: string) =>
        data.decisionToResults[decision] ?? [];

    const sections = outputs
        .map((output) => ({
            label: output.result,
            results: getDecisionResults(output.result),
            colors: answerColorToThemeColor(
                theme,
                store.getResultColor(output.result),
            ),
        }))
        .concat({
            label: "No Decision",
            results: data.noDecisionResults,
            colors: answerColorToThemeColor(theme),
        })
        .sort((a, b) => b.results.length - a.results.length);

    const hasErrors = data.errorResults.length > 0;
    const errorsSection = {
        label: "Errors",
        results: data.errorResults,
        colors: {
            color: theme.palette.red.main,
            borderColor: theme.palette.red.main,
            backgroundColor: theme.palette.red.background,
        },
    };

    return (
        <TitledSection
            title={
                <Grid
                    container
                    alignItems="center"
                    justifyContent="space-between"
                >
                    <Grid item>Results ({data.processedCount})</Grid>
                    <Grid item>
                        <ExportToCSV
                            data={Object.values(data.decisionToResults)
                                .flat()
                                .concat(
                                    data.noDecisionResults,
                                    data.errorResults,
                                )}
                            selectedKeys={store.selectedExportKeys}
                            onSelectedKeysChange={(keys) =>
                                store.setSelectedExportKeys(keys)
                            }
                            getLabelForKey={(key) => {
                                switch (key) {
                                    case "decision":
                                        return "Model Label";
                                    case "reasoning":
                                        return "Model Reasoning";
                                }
                            }}
                        />
                    </Grid>
                </Grid>
            }
            variant="bordered"
        >
            <Grid
                container
                direction="column"
                flexWrap="nowrap"
                rowGap={1}
                paddingBlock={2}
                paddingInline={2}
            >
                <Grid
                    container
                    item
                    columns={5}
                    columnSpacing={2}
                    rowSpacing={1}
                >
                    {hasErrors && (
                        <Grid item xs={1}>
                            <ScrollToResultButton
                                title={
                                    <Grid
                                        container
                                        alignItems="center"
                                        columnGap={1}
                                        color={errorsSection.colors.color}
                                    >
                                        <Error color="error" />
                                        {errorsSection.label}
                                    </Grid>
                                }
                                count={errorsSection.results.length}
                                scrollToId={getFriendlyId(errorsSection.label)}
                                sx={{
                                    border: `1px solid ${errorsSection.colors.borderColor}`,
                                    backgroundColor:
                                        errorsSection.colors.backgroundColor,
                                }}
                            />
                        </Grid>
                    )}
                    {sections.map((section) => (
                        <Grid item xs={1}>
                            <ScrollToResultButton
                                key={section.label}
                                title={section.label}
                                count={section.results.length}
                                scrollToId={getFriendlyId(section.label)}
                                sx={{
                                    border: `1px solid ${section.colors.borderColor}`,
                                    backgroundColor:
                                        section.colors.backgroundColor,
                                }}
                            />
                        </Grid>
                    ))}
                </Grid>
                <ErrorResultsSection results={errorsSection.results} />
                {sections.map((section) => (
                    <SuccessfulResultsSection
                        key={section.label}
                        results={section.results}
                        label={section.label}
                    />
                ))}
            </Grid>
        </TitledSection>
    );
});

const MetadataResultsView = observer(({ data }: Props) => {
    const theme = useTheme();
    const store = useStore(LLMClassifierBuilderStore);

    const hasErrors = data.errorResults.length > 0;
    const errorsSection = {
        label: "Errors",
        results: data.errorResults,
        colors: {
            color: theme.palette.red.main,
            borderColor: theme.palette.red.main,
            backgroundColor: theme.palette.red.background,
        },
    };

    const allResults = data.noDecisionResults.concat(
        Object.values(data.decisionToResults).flat(),
    );

    function extractWrittenMetadataClassifiers(): {
        classifier: ExtendedMetadataClassifier;
        values: unknown[];
    }[] {
        const classifierIdToValues: Record<string, unknown[]> = {};

        for (const result of allResults) {
            if (!result.executionResult) continue;

            for (const id of Object.keys(
                result.executionResult.extendedMetadataToWrite,
            )) {
                if (!Object.hasOwn(classifierIdToValues, id)) {
                    classifierIdToValues[id] = [];
                }

                classifierIdToValues[id].push(
                    result.executionResult.extendedMetadataToWrite[id],
                );
            }
        }

        return Object.entries(classifierIdToValues).map(([key, values]) => ({
            classifier: store.allExtendedMetadata.find((c) => c.id === key)!,
            values,
        }));
    }

    const metadataClassifiers = extractWrittenMetadataClassifiers();

    // Color palette for badges
    const colorPalette = [
        {
            borderColor: "#93C5FD",
            backgroundColor: "#DBEAFE",
        },
        {
            borderColor: "#86EFAC",
            backgroundColor: "#DCFCE7",
        },
        {
            borderColor: "#FDB195",
            backgroundColor: "#FFEDD5",
        },
        {
            borderColor: "#C4B5FD",
            backgroundColor: "#EDE9FE",
        },
        {
            borderColor: "#5EEAD4",
            backgroundColor: "#CCFBF1",
        },
        {
            borderColor: "#9CA3AF",
            backgroundColor: "#E5E7EB",
        },
    ];

    return (
        <Stack rowGap={2}>
            <TitledSection
                title={
                    <Stack
                        direction="row"
                        alignItems="center"
                        justifyContent="space-between"
                        width="100%"
                    >
                        <Typography
                            variant="body1"
                            fontWeight={600}
                            fontSize="16px"
                            lineHeight="24px"
                            color={theme.palette.neutral[700]}
                        >
                            Results ({data.processedCount})
                        </Typography>
                        <ExportToCSV
                            data={Object.values(data.decisionToResults)
                                .flat()
                                .concat(
                                    data.noDecisionResults,
                                    data.errorResults,
                                )}
                            selectedKeys={store.selectedExportKeys}
                            onSelectedKeysChange={(keys) =>
                                store.setSelectedExportKeys(keys)
                            }
                            getLabelForKey={(key) => {
                                switch (key) {
                                    case "decision":
                                        return "Model Label";
                                    case "reasoning":
                                        return "Model Reasoning";
                                }
                            }}
                        />
                    </Stack>
                }
                variant="bordered"
            >
                <Stack
                    rowGap={3}
                    paddingBlock={2}
                    paddingInline={2}
                    sx={{ width: "100%" }}
                >
                    {metadataClassifiers.map(({ classifier, values }) => {
                        // Check if values are objects or arrays (non-primitive)
                        const hasComplexValues = values.some(
                            (value) =>
                                typeof value === "object" && value !== null,
                        );

                        let sortedEntries: [string, number][] | undefined;

                        if (!hasComplexValues) {
                            // Count occurrences of each unique value
                            const valueCounts = values.reduce(
                                (acc: Record<string, number>, value) => {
                                    const key = String(value);
                                    acc[key] = (acc[key] || 0) + 1;
                                    return acc;
                                },
                                {} as Record<string, number>,
                            );

                            // Sort by count descending
                            sortedEntries = Object.entries(valueCounts).sort(
                                (a, b) => b[1] - a[1],
                            );
                        }

                        return (
                            <Stack key={classifier.id} rowGap={1.5}>
                                <Stack
                                    direction="row"
                                    columnGap={1}
                                    alignItems="center"
                                >
                                    <Typography
                                        fontWeight={700}
                                        fontSize="12px"
                                        color={theme.palette.gray[500]}
                                    >
                                        Assigned Extended Metadata Field:
                                    </Typography>
                                    <ResultBadge>
                                        {classifier.name}
                                        {hasComplexValues
                                            ? ` (${values.length})`
                                            : ""}
                                    </ResultBadge>
                                </Stack>
                                {sortedEntries && (
                                    <Grid
                                        container
                                        columnSpacing={2}
                                        rowSpacing={1.5}
                                        columns={4}
                                    >
                                        {sortedEntries.map(
                                            ([value, count], index) => {
                                                const colors =
                                                    colorPalette[
                                                        index %
                                                            colorPalette.length
                                                    ];
                                                return (
                                                    <Grid
                                                        item
                                                        xs={1}
                                                        key={value}
                                                    >
                                                        <Grid
                                                            container
                                                            justifyContent="space-between"
                                                            alignItems="center"
                                                            paddingInline={2}
                                                            paddingBlock={1}
                                                            borderRadius={1}
                                                            sx={{
                                                                border: `1px solid ${colors.borderColor}`,
                                                                backgroundColor:
                                                                    colors.backgroundColor,
                                                            }}
                                                        >
                                                            <Grid item>
                                                                <Typography
                                                                    sx={{
                                                                        fontSize:
                                                                            "14px",
                                                                        fontWeight: 600,
                                                                        color: "#6B7280",
                                                                    }}
                                                                >
                                                                    {value}
                                                                </Typography>
                                                            </Grid>
                                                            <Grid item>
                                                                <Typography
                                                                    sx={{
                                                                        fontSize:
                                                                            "20px",
                                                                        fontWeight: 600,
                                                                        color: "#374151",
                                                                    }}
                                                                >
                                                                    {count}
                                                                </Typography>
                                                            </Grid>
                                                        </Grid>
                                                    </Grid>
                                                );
                                            },
                                        )}
                                    </Grid>
                                )}
                            </Stack>
                        );
                    })}
                    <Grid
                        container
                        columns={4}
                        columnSpacing={2}
                        rowSpacing={1}
                    >
                        {hasErrors && (
                            <Grid item xs={1}>
                                <ScrollToResultButton
                                    title={
                                        <Grid
                                            container
                                            alignItems="center"
                                            columnGap={1}
                                            color={errorsSection.colors.color}
                                        >
                                            <Error color="error" />
                                            {errorsSection.label}
                                        </Grid>
                                    }
                                    count={errorsSection.results.length}
                                    scrollToId={getFriendlyId(
                                        errorsSection.label,
                                    )}
                                    sx={{
                                        border: `1px solid ${errorsSection.colors.borderColor}`,
                                        backgroundColor:
                                            errorsSection.colors
                                                .backgroundColor,
                                    }}
                                />
                            </Grid>
                        )}
                    </Grid>
                </Stack>
            </TitledSection>
            <ErrorResultsSection results={errorsSection.results} />
            {allResults.length > 0 && (
                <Paper
                    sx={(theme) => ({
                        borderRadius: theme.shape.borderRadius / 2,
                        boxShadow: "0px 1px 2px 0px rgba(0, 0, 0, 0.07)",
                        border: `1px solid ${theme.palette.lightgrayBorder.main}`,
                        padding: 2,
                    })}
                >
                    <MetadataResultsCards results={allResults} />
                </Paper>
            )}
        </Stack>
    );
});
